import { useTranslation } from "react-i18next";
import VerticalLanding, { type VerticalCopy } from "./VerticalLanding";

type Lang = "de" | "en";
const useLang = (): Lang => {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith("de") ? "de" : "en";
};

const copy: Record<Lang, VerticalCopy> = {
  de: {
    eyebrow: "FÜR COOs, HEADS OF OPERATIONS & DIGITAL",
    h1a: "Ein wiederkehrender Prozess —",
    h1b: "läuft als autonomes ",
    h1hl: "System.",
    h1c: "In Ihrer Cloud. In 90 Tagen.",
    lead: "Wir übernehmen einen Ops-Prozess Ihres Unternehmens — von Angebotserstellung bis Support-Deflection — als autonomes Agenten-System. In Ihrer Cloud. Mit Ihren Regeln. Zum Festpreis.",
    cta: "Erstgespräch anfragen",
    ctaNote: "// 30 Min. — kein Verkaufsgespräch",
    stackLine: "// Anbindung: SAP · Salesforce · ServiceNow · Microsoft 365 · Ihre APIs",
    stats: [
      { num: "50 %+", label: "aller GenAI-Projekte werden nach dem Pilot abgebrochen.", src: "Gartner, 2024" },
      { num: "80,3 %", label: "der Mitarbeiter nutzen KI — aber im Browser, nicht im Prozess.", src: "RAND / Stanford, 2024" },
      { num: "24/7", label: "Betriebsfenster für Prozesse, die heute Bürozeiten haben.", src: "Ops Design Baseline" },
    ],
    loop: { title: "system/loop — ops_runner", running: "RUNNING" },
    stages: [
      { icon: "◉", name: "detect", sub: "Trigger erkannt" },
      { icon: "⚡", name: "trigger", sub: "Job gestartet" },
      { icon: "✎", name: "draft", sub: "Antwort entworfen" },
      { icon: "✓", name: "approve", sub: "Freigabe Fachbereich", gate: true },
      { icon: "▶", name: "execute", sub: "In Systeme geschrieben" },
      { icon: "◫", name: "measure", sub: "SLA getrackt" },
    ],
    gateTag: "HITL",
    log: {
      detect: "Trigger: neue Support-Ticket-Kategorie ‚Rechnung falsch'",
      trigger: "Job #O-9042 in Warteschlange — SLA: 4h",
      draft: "Antwort aus Wissensdatenbank + Kunden-Historie generiert",
      approve: "Team Lead Support hat freigegeben",
      execute: "Antwort versandt · Ticket in ServiceNow geschlossen",
      measure: "Bearbeitung: 6 Min · SLA erfüllt · CSAT 4.6",
    },
    problem: {
      eyebrow: "// DIAGNOSE",
      h2a: "Das Problem ist nicht KI.",
      h2b: "Das Problem ist Pilot-Purgatorium.",
      lead: "Sie haben Copilot, Sie haben PoCs, Sie haben Berater. Nichts davon läuft im Kerngeschäft — weil ein Chat kein Prozess ist und ein Pilot kein System.",
      cards: [
        { tag: "// PoC-FALLE", title: "Mindestens die Hälfte wird nie produktiv.", body: "Gartner: Bis Ende 2025 werden mindestens 50 % aller GenAI-Projekte nach dem Pilot abgebrochen — schlechter Daten-Fit, unklarer ROI, keine Integration." },
        { tag: "// CHAT-FALLE", title: "80,3 % nutzen KI. Aber nicht im Prozess.", body: "RAND/Stanford: 80,3 % der Mitarbeiter nutzen generative KI — als Assistent im Browser. Der Prozess selbst läuft weiter per Ticket, Excel, E-Mail." },
        { tag: "// GOVERNANCE-FALLE", title: "Sicherheit blockt, bevor Wert entsteht.", body: "Compliance sagt Nein zur SaaS-Blackbox. Zurecht. Ergebnis: nichts geht live, weil nichts in der eigenen Cloud gebaut wurde." },
      ],
      note1: "Wir bauen keinen weiteren Piloten. Wir übernehmen einen Prozess — nicht als Assistent, sondern als System. In Ihrer Cloud, mit Freigabestellen, integriert in SAP/Salesforce/ServiceNow.",
      note2: "Am Ende der 90 Tage läuft es. Oder Sie zahlen nicht.",
    },
    mechanism: {
      eyebrow: "// SO FUNKTIONIERT ES",
      h2: "Sechs Stufen. Eine Schleife. Ein Prozess.",
      lead: "Ein wiederkehrender Ops-Prozess wird zerlegt, jede Stufe autonom ausgeführt, eine Stufe bleibt beim Fachbereich: die Freigabe.",
      rows: [
        { name: "detect", desc: "System hört Ticket-System / Postfach / API / Event-Bus ab und erkennt Trigger automatisch." },
        { name: "trigger", desc: "Für jeden Trigger wird ein Job angelegt, priorisiert, gemäß Ihrer SLAs gequeued." },
        { name: "draft", desc: "Aus Stammdaten, Historie und Ihren Business-Regeln entsteht eine Antwort / Aktion / Freigabevorlage." },
        { name: "approve", desc: "Fachbereich bekommt Vorschlag mit Kontext zur Freigabe. Diese Stufe bleibt beim Menschen — mit voller Audit-Spur.", gate: true },
        { name: "execute", desc: "Nach OK wird in Ihre Systeme geschrieben (SAP, Salesforce, ServiceNow) — ohne manuelles Copy-Paste." },
        { name: "measure", desc: "Durchlaufzeit, SLA-Erfüllung, Qualitätsmetriken pro Job gemessen. Revisionssicher archiviert." },
      ],
    },
    useCases: {
      eyebrow: "// EINSATZFELDER",
      h2: "Was wir für Enterprises bauen.",
      lead: "Vier Loop-Familien, die in jedem größeren Unternehmen dieselben Ressourcen binden.",
      items: [
        { tag: "// QUOTE-TO-CASH", title: "Angebotserstellung, autonom.", body: "Anfragen aus Postfach, CRM oder Portal werden analysiert, kalkuliert und als versandreife Angebote vorgelegt — Vertrieb gibt frei." },
        { tag: "// SUPPORT DEFLECTION", title: "Tier-1-Anfragen ohne Warteschleife.", body: "Wiederkehrende Support-Fälle werden aus Wissensbasis und Kundenkontext beantwortet. Team Lead gibt in einem Klick frei." },
        { tag: "// COMPLIANCE OPS", title: "Reviews & Freigaben mit Audit-Spur.", body: "Verträge, Marketing-Assets, Fördermittelanträge — automatischer Vorab-Check gegen Ihre Policies, revisionssicher dokumentiert." },
        { tag: "// AI-VISIBILITY", title: "Marken-Sichtbarkeit in ChatGPT & Co.", body: "Wenn ChatGPT den Wettbewerber empfiehlt, ist das kein Marketing-Problem, sondern ein strategisches. Als eigenes System oder über webblitz.ai.", link: { label: "webblitz.ai ansehen", href: "https://webblitz.ai" }, external: true },
      ],
    },
    outcome: {
      eyebrow: "// ERGEBNIS",
      h2: "Was Sie am Tag 91 haben.",
      cards: [
        { ic: "SLA", title: "Durchlaufzeit in Minuten statt Tagen.", body: "Prozesse, die heute nach Sachbearbeiter-Kapazität laufen, laufen dann nach Trigger. Nacht, Wochenende, Peak — egal." },
        { ic: "GOV", title: "Volle Kontrolle, volle Audit-Spur.", body: "Human-in-the-Loop an kritischen Stellen. Jede Entscheidung geloggt und begründet. Compliance sagt Ja, nicht Nein." },
        { ic: "OWN", title: "Das System läuft in Ihrer Cloud.", body: "AWS, Azure, GCP oder on-prem. Keine SaaS-Blackbox, keine Datenabflüsse, kein Lock-in. Ihr Betriebssystem, nicht das eines Anbieters." },
      ],
    },
    mid: {
      eyebrow: "// NÄCHSTER SCHRITT",
      h3: "Welcher Prozess ist reif für die Schleife?",
      lead: "30 Min. Erstgespräch. Wir schauen uns einen konkreten Ops-Prozess an und sagen ehrlich, ob und wo sich das rechnet.",
      cta: "Erstgespräch anfragen",
    },
    phases: {
      eyebrow: "// PROGRAMM",
      h2: "90 Tage. Drei Phasen. Ein Festpreis. Am Ende läuft es.",
      lead: "Keine Roadmap, kein PoC, kein ‚wir sehen dann weiter'. Ein klarer Bauplan mit definiertem Ende.",
      p: [
        { days: "TAG 1–30", title: "Prozess-Cut.", bullets: ["Einen Prozess auswählen und schneiden.", "Datenpfade, System-Anbindungen, Policies klären.", "Approve-Gates mit dem Fachbereich definieren."] },
        { days: "TAG 31–60", title: "Build.", bullets: ["Loop implementieren, in Ihrer Cloud, mit Ihren Daten.", "Regeln aus Historie und Ihren Policies ableiten.", "Wöchentliche Demos — kein Blackbox-Build."] },
        { days: "TAG 61–90", title: "Live.", bullets: ["Schrittweise Übergabe: Schatten-Betrieb, dann Voll-Betrieb.", "Runbook, Monitoring, Alerting, Audit.", "Ihr Betriebsteam übernimmt. Wir bleiben optional als Service."] },
      ],
    },
    buying: {
      eyebrow: "// SO KAUFEN SIE DAS",
      h2: "Kein Abo. Kein Preis pro Vorgang. Keine Mindestlaufzeit.",
      p1: "Ein Festpreis für den 90-Tage-Build. Das System läuft in Ihrer Cloud und gehört Ihnen — Betrieb und Weiterentwicklung optional als monatlicher Service.",
      p2: "Bilanziell ein Asset, nicht ein OpEx-Rinnsal, das nie aufhört.",
      pills: ["Festpreis", "In Ihrer Cloud", "Ihr Eigentum", "DSGVO-konform"],
    },
    guarantee: {
      eyebrow: "// GARANTIE",
      h2: "Am Tag 91 läuft es. Oder Sie zahlen nicht.",
      body: "Wenn das System nach 90 Tagen nicht produktiv im Betrieb ist, bekommen Sie den Festpreis zurück. Ohne Diskussion.",
    },
    final: {
      brand: "// RAW RESPONSE LABS × IHR UNTERNEHMEN",
      h2a: "Ein Prozess. Ein System.",
      h2b: "Ein ",
      h2hl: "Festpreis.",
      h2c: " 90 Tage.",
      lead: "30 Min. Erstgespräch. Wir schauen uns einen konkreten Prozess an. Am Ende wissen Sie, ob und wie sich das für Sie rechnet.",
      cta: "Erstgespräch anfragen",
      ctaNote: "// Kein Verkaufsgespräch. Kein Pitch. Ein Blick auf Ihren Prozess.",
      sticky: "Erstgespräch",
    },
  },
  en: {
    eyebrow: "FOR COOs, HEADS OF OPERATIONS & DIGITAL",
    h1a: "A recurring process —",
    h1b: "run by an autonomous ",
    h1hl: "system.",
    h1c: "In your cloud. In 90 days.",
    lead: "We take over one ops process in your company — from quoting to support deflection — as an autonomous agent system. In your cloud. With your rules. At a fixed price.",
    cta: "Book intro call",
    ctaNote: "// 30 min — no sales pitch",
    stackLine: "// Integrations: SAP · Salesforce · ServiceNow · Microsoft 365 · your APIs",
    stats: [
      { num: "50%+", label: "of all GenAI projects are abandoned after the pilot.", src: "Gartner, 2024" },
      { num: "80.3%", label: "of employees use AI — but in the browser, not in the process.", src: "RAND / Stanford, 2024" },
      { num: "24/7", label: "operating window for processes that run office hours today.", src: "Ops Design Baseline" },
    ],
    loop: { title: "system/loop — ops_runner", running: "RUNNING" },
    stages: [
      { icon: "◉", name: "detect", sub: "Trigger detected" },
      { icon: "⚡", name: "trigger", sub: "Job queued" },
      { icon: "✎", name: "draft", sub: "Response drafted" },
      { icon: "✓", name: "approve", sub: "Team lead approves", gate: true },
      { icon: "▶", name: "execute", sub: "Written to systems" },
      { icon: "◫", name: "measure", sub: "SLA tracked" },
    ],
    gateTag: "HITL",
    log: {
      detect: "Trigger: new support ticket category ‘invoice wrong'",
      trigger: "Job #O-9042 queued — SLA: 4h",
      draft: "Response generated from knowledge base + customer history",
      approve: "Support team lead approved",
      execute: "Response sent · ticket closed in ServiceNow",
      measure: "Handled in 6 min · SLA met · CSAT 4.6",
    },
    problem: {
      eyebrow: "// DIAGNOSIS",
      h2a: "The problem isn't AI.",
      h2b: "The problem is pilot purgatory.",
      lead: "You have Copilot, you have PoCs, you have consultants. None of it runs in the core business — because a chat isn't a process and a pilot isn't a system.",
      cards: [
        { tag: "// POC TRAP", title: "At least half never make it to production.", body: "Gartner: by end of 2025 at least 50% of GenAI projects will be abandoned after the pilot — poor data fit, unclear ROI, no integration." },
        { tag: "// CHAT TRAP", title: "80.3% use AI. But not in the process.", body: "RAND/Stanford: 80.3% of employees use generative AI — as a browser assistant. The process itself still runs on tickets, Excel and email." },
        { tag: "// GOVERNANCE TRAP", title: "Security blocks before value is created.", body: "Compliance says no to SaaS black boxes. Rightly so. Result: nothing goes live, because nothing was built in your own cloud." },
      ],
      note1: "We don't build another pilot. We take over a process — not as an assistant, but as a system. In your cloud, with approval gates, integrated with SAP/Salesforce/ServiceNow.",
      note2: "At the end of 90 days it runs. Or you don't pay.",
    },
    mechanism: {
      eyebrow: "// HOW IT WORKS",
      h2: "Six stages. One loop. One process.",
      lead: "A recurring ops process is decomposed, each stage runs autonomously, one stage stays with the business unit: the approval.",
      rows: [
        { name: "detect", desc: "System listens to ticket system / inbox / API / event bus and detects triggers automatically." },
        { name: "trigger", desc: "For each trigger a job is created, prioritised, queued against your SLAs." },
        { name: "draft", desc: "From master data, history and your business rules, a response / action / approval draft is generated." },
        { name: "approve", desc: "Business unit gets the proposal with full context to approve. This stage stays with the human — with full audit trail.", gate: true },
        { name: "execute", desc: "After OK it writes back into your systems (SAP, Salesforce, ServiceNow) — no manual copy-paste." },
        { name: "measure", desc: "Cycle time, SLA compliance, quality metrics measured per job. Audit-safe archived." },
      ],
    },
    useCases: {
      eyebrow: "// WHERE IT FITS",
      h2: "What we build for enterprises.",
      lead: "Four loop families that bind the same resources in every larger organisation.",
      items: [
        { tag: "// QUOTE-TO-CASH", title: "Autonomous quoting.", body: "Requests from inbox, CRM or portal are analysed, priced and drafted as send-ready quotes — sales approves." },
        { tag: "// SUPPORT DEFLECTION", title: "Tier-1 requests without the queue.", body: "Recurring support cases are answered from the knowledge base and customer context. Team lead approves in one click." },
        { tag: "// COMPLIANCE OPS", title: "Reviews & approvals with audit trail.", body: "Contracts, marketing assets, grant applications — automatic pre-check against your policies, audit-safe documented." },
        { tag: "// AI-VISIBILITY", title: "Brand visibility in ChatGPT & co.", body: "When ChatGPT recommends the competitor, that's not a marketing problem, it's a strategic one. As your own system or via webblitz.ai.", link: { label: "See webblitz.ai", href: "https://webblitz.ai" }, external: true },
      ],
    },
    outcome: {
      eyebrow: "// OUTCOME",
      h2: "What you have on day 91.",
      cards: [
        { ic: "SLA", title: "Cycle time in minutes, not days.", body: "Processes that run on caseworker capacity today run on triggers. Night, weekend, peak — irrelevant." },
        { ic: "GOV", title: "Full control, full audit trail.", body: "Human-in-the-loop at critical points. Every decision logged and reasoned. Compliance says yes, not no." },
        { ic: "OWN", title: "The system runs in your cloud.", body: "AWS, Azure, GCP or on-prem. No SaaS black box, no data drain, no lock-in. Your operating system, not a vendor's." },
      ],
    },
    mid: {
      eyebrow: "// NEXT STEP",
      h3: "Which process is ready for the loop?",
      lead: "30 min intro call. We look at one concrete ops process and tell you honestly whether and where it pays off.",
      cta: "Book intro call",
    },
    phases: {
      eyebrow: "// PROGRAM",
      h2: "90 days. Three phases. One fixed price. At the end it runs.",
      lead: "No roadmap, no PoC, no ‘let's see'. A clear blueprint with a defined end.",
      p: [
        { days: "DAY 1–30", title: "Process cut.", bullets: ["Pick one process and cut it.", "Clarify data paths, system integrations, policies.", "Define approve gates with the business unit."] },
        { days: "DAY 31–60", title: "Build.", bullets: ["Implement the loop, in your cloud, with your data.", "Derive rules from history and your policies.", "Weekly demos — no black-box build."] },
        { days: "DAY 61–90", title: "Live.", bullets: ["Gradual handover: shadow mode, then full ops.", "Runbook, monitoring, alerting, audit.", "Your ops team takes over. We stay optional as a service."] },
      ],
    },
    buying: {
      eyebrow: "// HOW YOU BUY THIS",
      h2: "No subscription. No per-transaction price. No minimum term.",
      p1: "A fixed price for the 90-day build. The system runs in your cloud and belongs to you — operation and further development optional as a monthly service.",
      p2: "An asset on your balance sheet, not an OpEx trickle that never ends.",
      pills: ["Fixed price", "In your cloud", "You own it", "GDPR-compliant"],
    },
    guarantee: {
      eyebrow: "// GUARANTEE",
      h2: "On day 91 it runs. Or you don't pay.",
      body: "If the system isn't in productive operation after 90 days, you get the fixed price back. No discussion.",
    },
    final: {
      brand: "// RAW RESPONSE LABS × YOUR ENTERPRISE",
      h2a: "One process. One system.",
      h2b: "One ",
      h2hl: "fixed price.",
      h2c: " 90 days.",
      lead: "30 min intro call. We look at one concrete process. At the end you know if and how it pays off for you.",
      cta: "Book intro call",
      ctaNote: "// No sales pitch. No deck. A look at your process.",
      sticky: "Intro call",
    },
  },
};

const EnterpriseLanding = () => {
  const lang = useLang();
  return <VerticalLanding c={copy[lang]} />;
};

export default EnterpriseLanding;
